import { type FormEvent } from "react"
import { Link, useForm } from "@inertiajs/react"
import { z } from "zod"
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Select,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes"
import { ProjectColorSchema, ProjectFormSchema, ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  colors: z.array(ProjectColorSchema),
  form: ProjectFormSchema.nullish(),
})

type Props = z.infer<typeof PropsSchema>
type ProjectColor = z.infer<typeof ProjectColorSchema>

const colorLabels: Record<ProjectColor, string> = {
  indigo: "Indigo",
  blue: "Blue",
  green: "Green",
  orange: "Orange",
  crimson: "Crimson",
}

function formatError(error: string | undefined) {
  return error ?? null
}

export default function EditProjectPage(rawProps: Props) {
  const props = PropsSchema.parse(rawProps)

  const { data, setData, patch, processing, errors } = useForm({
    name: props.form?.name ?? props.project.name,
    description: props.form?.description ?? props.project.description ?? "",
    color: props.form?.color ?? props.project.color,
  })

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    patch(`/projects/${props.project.id}`)
  }

  return (
    <Flex justify="center">
      <Box width="100%" style={{ maxWidth: "640px" }}>
        <Flex justify="between" align="center" mb="4">
          <Heading size="7">Edit project</Heading>
          <Button variant="soft" color="gray" asChild>
            <Link href="/dashboard">Back to dashboard</Link>
          </Button>
        </Flex>

        <Card size="4">
          <Box asChild>
            <form onSubmit={submit}>
              <Flex direction="column" gap="4">
                <label>
                  <Text as="div" size="2" weight="medium" mb="1">
                    Title
                  </Text>
                  <TextField.Root
                    value={data.name}
                    onChange={(event) => setData("name", event.currentTarget.value)}
                    placeholder="Project title"
                    required
                  />
                  {formatError(errors.name) && (
                    <Text as="div" size="1" color="red" mt="1">
                      {formatError(errors.name)}
                    </Text>
                  )}
                </label>

                <label>
                  <Text as="div" size="2" weight="medium" mb="1">
                    Description
                  </Text>
                  <TextArea
                    value={data.description}
                    onChange={(event) => setData("description", event.currentTarget.value)}
                    placeholder="Describe what this project is about"
                    rows={4}
                  />
                  {formatError(errors.description) && (
                    <Text as="div" size="1" color="red" mt="1">
                      {formatError(errors.description)}
                    </Text>
                  )}
                </label>

                <label>
                  <Text as="div" size="2" weight="medium" mb="1">
                    Color
                  </Text>
                  <Select.Root value={data.color} onValueChange={(value) => setData("color", value as ProjectColor)}>
                    <Select.Trigger />
                    <Select.Content>
                      {props.colors.map((color) => (
                        <Select.Item key={color} value={color}>
                          {colorLabels[color]}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                  {formatError(errors.color) && (
                    <Text as="div" size="1" color="red" mt="1">
                      {formatError(errors.color)}
                    </Text>
                  )}
                </label>

                <Flex gap="3" justify="end">
                  <Button type="submit" variant="classic" disabled={processing}>
                    {processing ? "Saving changes..." : "Save changes"}
                  </Button>
                </Flex>
              </Flex>
            </form>
          </Box>
        </Card>
      </Box>
    </Flex>
  )
}
